import { useEffect } from "react";

/**
 * Lightweight SEO component - updates <title>, <meta description>, canonical URL,
 * and OG/Twitter tags per-route. No dependencies (no react-helmet needed).
 *
 * Usage: <SEO title="..." description="..." path="/classic" noindex />
 */
export default function SEO({
  title,
  description,
  path = "",
  image = "https://greinchww.com/greinch-logo-light.png",
  type = "website",
  noindex = false,
}) {
  useEffect(() => {
    const canonical = `https://greinchww.com${path}`;
    const finalTitle = title ? `${title} | Greinch WW LLC` : "Greinch WW LLC | You ask, we develop.";

    // Title
    document.title = finalTitle;

    // Helper to set/update a meta tag
    const upsert = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs).forEach(([k, v]) => k !== "content" && el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      el.setAttribute("content", attrs.content);
    };

    if (description) {
      upsert('meta[name="description"]', { name: "description", content: description });
      upsert('meta[property="og:description"]', { property: "og:description", content: description });
      upsert('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    }
    upsert('meta[property="og:title"]', { property: "og:title", content: finalTitle });
    upsert('meta[name="twitter:title"]', { name: "twitter:title", content: finalTitle });
    upsert('meta[property="og:url"]', { property: "og:url", content: canonical });
    upsert('meta[property="og:type"]', { property: "og:type", content: type });
    upsert('meta[property="og:image"]', { property: "og:image", content: image });
    upsert('meta[name="twitter:image"]', { name: "twitter:image", content: image });
    upsert('meta[name="robots"]', {
      name: "robots",
      content: noindex ? "noindex, nofollow" : "index, follow, max-snippet:-1, max-image-preview:large",
    });

    // Canonical link
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonical);
  }, [title, description, path, image, type, noindex]);

  return null;
}
